import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Heart, WifiOff, Trash2, Mountain, Tent, Trees, HardDrive, Download, Droplets, Coffee } from "lucide-react";
import { OthalaIcon } from "../components/NorseIcons";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { getAnonFavs, getOffline, removeOffline } from "../lib/storage";
import { coverImageFor, isFallbackImage } from "../lib/stockImages";
import { useOfflineIds } from "../lib/useOfflineIds";
import SamplePhotoTag from "../components/SamplePhotoTag";

function formatBytes(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Favourites() {
  const { user } = useAuth();
  const [offlineMap, setOfflineMap] = useState(() => getOffline());
  const { ids: offlineIds, total_bytes, count, records, refresh: refreshOffline } = useOfflineIds();

  useEffect(() => {
    const h = () => setOfflineMap(getOffline());
    window.addEventListener("td-offline-changed", h);
    return () => window.removeEventListener("td-offline-changed", h);
  }, []);

  const remote = useQuery({
    queryKey: ["favs"],
    enabled: !!user,
    queryFn: async () => (await api.get("/favorites")).data,
  });

  const [anonItems, setAnonItems] = useState([]);
  const anonIds = !user ? getAnonFavs() : [];
  useEffect(() => {
    if (user) return;
    if (!anonIds.length) { setAnonItems([]); return; }
    Promise.all(anonIds.map((id) => api.get(`/locations/${id}`).then((r) => r.data).catch(() => null)))
      .then((arr) => setAnonItems(arr.filter(Boolean)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, anonIds.length]);

  const favItems = user ? (remote.data?.items || []) : anonItems;
  const idbEntries = records; // rich IDB records with polyline + deep link
  const legacyEntries = Object.entries(offlineMap).filter(([id]) => !offlineIds.has(id));

  const clearAllOffline = () => {
    if (!count) return;
    if (!window.confirm(`Delete all ${count} offline saved walk${count === 1 ? "" : "s"}? This frees ${formatBytes(total_bytes)} of phone storage.`)) return;
    idbEntries.forEach((r) => removeOffline(r.location_id));
    legacyEntries.forEach(([id]) => removeOffline(id));
    setTimeout(refreshOffline, 200);
    toast.success("Offline library cleared");
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <header className="mb-5">
        <div className="td-section-label">Your basecamp</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-brand-text mt-1">Saved walks</h1>
      </header>

      {/* Offline storage summary — always visible so users know their footprint. */}
      <section className="td-card p-4 sm:p-5 mb-5" data-testid="offline-storage-panel">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-brand-primary/10 flex items-center justify-center shrink-0">
            <HardDrive size={18} className="text-brand-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="td-section-label">Offline library</div>
            <div className="font-heading text-lg text-brand-text mt-0.5" data-testid="offline-storage-total">
              {count} walk{count === 1 ? "" : "s"} · {formatBytes(total_bytes)} on your phone
            </div>
          </div>
          {count > 0 && (
            <button
              data-testid="offline-clear-all"
              onClick={clearAllOffline}
              className="td-btn-secondary !py-2 !px-3 !text-xs !text-brand-error !border-brand-error/40 whitespace-nowrap"
            >
              <Trash2 size={13} /> Free up space
            </button>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <Heart size={16} className="text-brand-accent" />
          <h2 className="font-heading text-lg">Favourites</h2>
        </div>
        {favItems.length === 0 ? (
          <div className="td-card p-6 text-brand-subtle text-sm">
            Tap the heart on any walk to save it here. {!user && "Sign in to sync across devices."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {favItems.map((l) => (
              <Link
                key={l.location_id}
                to={`/location/${l.location_id}`}
                className="td-card overflow-hidden flex items-center gap-3 p-3 hover:-translate-y-0.5 transition-all"
                data-testid={`fav-item-${l.location_id}`}
              >
                <div className="relative w-20 h-20 rounded-2xl overflow-hidden bg-brand-border/30 shrink-0">
                  <img src={coverImageFor(l)} alt="" className="w-full h-full object-cover" />
                  {isFallbackImage(l) && (
                    <SamplePhotoTag
                      testId={`sample-photo-tag-fav-${l.location_id}`}
                      className="!bottom-1 !left-1 !px-1 !py-0 !text-[9px]"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-brand-subtle flex items-center gap-1">
                    {l.type === "camping" ? <Tent size={12} /> : <Mountain size={12} />} {l.region}
                  </div>
                  <div className="font-heading text-base truncate">{l.name}</div>
                  {offlineIds.has(l.location_id) && (
                    <div
                      data-testid={`fav-offline-badge-${l.location_id}`}
                      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-primary/10 text-brand-primary border border-brand-primary/30"
                    >
                      <Download size={10} /> Ready offline
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mt-8">
        <div className="flex items-center gap-2 mb-3">
          <WifiOff size={16} className="text-brand-primary" />
          <h2 className="font-heading text-lg">Cached routes</h2>
        </div>
        {idbEntries.length === 0 && legacyEntries.length === 0 ? (
          <div className="td-card p-6 text-brand-subtle text-sm">
            Save a walk for offline from its detail page to keep route, weather and the Google Maps hand-off available without signal.
          </div>
        ) : (
          <div className="space-y-2">
            {idbEntries.map((r) => {
              const bytes = (() => { try { return JSON.stringify(r).length; } catch { return 0; } })();
              // Basecamps live under bc:<id> in IDB so they don't collide with
              // trail location_ids. Strip the prefix to get the routable id.
              const isBasecamp = r.type === "basecamp";
              const linkTo = isBasecamp
                ? `/basecamp/${(r.basecamp?.basecamp_id) || String(r.location_id).replace(/^bc:/, "")}`
                : `/location/${r.location_id}`;
              const TypeIcon = isBasecamp ? OthalaIcon : (r.type === "camping" ? Tent : Mountain);
              // A tiny visual summary of what a walker will find at this
              // basecamp — reads at a glance without opening the detail page.
              const bcAmenityChips = isBasecamp && r.basecamp ? [
                r.basecamp.has_water_tap && { icon: Droplets, label: "Water" },
                r.basecamp.cafe_on_site && { icon: Coffee, label: "Cafe" },
                r.basecamp.has_toilets && { label: "Toilets" },
                r.basecamp.has_car_park && { label: "Car park" },
                r.basecamp.pub_nearby && { label: "Pub nearby" },
              ].filter(Boolean).slice(0, 4) : [];
              return (
                <div
                  key={r.location_id}
                  className="td-card p-4 flex items-center gap-3"
                  data-testid={`offline-item-${r.location_id}`}
                >
                  <Link to={linkTo} className="flex-1 min-w-0">
                    <div className="text-[11px] text-brand-subtle flex items-center gap-1">
                      <TypeIcon size={isBasecamp ? 16 : 11} className={isBasecamp ? "text-brand-accent" : "text-brand-primary"} />
                      {isBasecamp ? "Basecamp" : (r.region || "United Kingdom")}
                    </div>
                    <div className="font-heading text-base truncate mt-0.5" data-testid={`offline-item-name-${r.location_id}`}>
                      {r.name || "Saved walk"}
                    </div>
                    <div className="text-xs text-brand-subtle">
                      {formatBytes(bytes)}
                      {r.saved_at && <> · saved {new Date(r.saved_at).toLocaleDateString("en-GB")}</>}
                    </div>
                    {bcAmenityChips.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`offline-item-basecamp-amenities-${r.location_id}`}>
                        {bcAmenityChips.map((c, i) => {
                          const Icon = c.icon;
                          return (
                            <span
                              key={i}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-brand-primary/10 text-brand-primary border border-brand-primary/25"
                            >
                              {Icon && <Icon size={9} />} {c.label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </Link>
                  <button
                    data-testid={`offline-remove-${r.location_id}`}
                    onClick={() => removeOffline(r.location_id)}
                    className="text-brand-subtle hover:text-brand-error p-2"
                    aria-label="Remove"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              );
            })}
            {legacyEntries.map(([id, entry]) => (
              <div
                key={id}
                className="td-card p-4 flex items-center gap-3"
                data-testid={`offline-item-${id}`}
              >
                <Link to={`/location/${id}`} className="flex-1 min-w-0">
                  <div className="font-heading text-base truncate">{entry.location?.name || "Saved walk"}</div>
                  <div className="text-xs text-brand-subtle">
                    Legacy cache · saved {new Date(entry._saved_at).toLocaleDateString("en-GB")}
                  </div>
                </Link>
                <button
                  data-testid={`offline-remove-${id}`}
                  onClick={() => removeOffline(id)}
                  className="text-brand-subtle hover:text-brand-error p-2"
                  aria-label="Remove"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
